import { lazy } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from '@/modules/auth/AuthContext'
import { ToastProvider } from '@/components/project/Toast'
import { UploadProvider } from '@/modules/upload/UploadContext'
import { ProtectedRoute } from '@/modules/auth/ProtectedRoute'
import { Layout } from '@/components/project/Layout'
import { LoginPage } from '@/modules/auth/LoginPage'
import { RegisterPage } from '@/modules/auth/RegisterPage'
import { VerifyEmailPage } from '@/modules/auth/VerifyEmailPage'
import { ForgotPasswordPage } from '@/modules/auth/ForgotPasswordPage'
import { ResetPasswordPage } from '@/modules/auth/ResetPasswordPage'

const CatalogPage = lazy(() => import('@/modules/projects/CatalogPage').then(m => ({ default: m.CatalogPage })))
const DocumentsPage = lazy(() => import('@/modules/documents/DocumentsPage').then(m => ({ default: m.DocumentsPage })))
const AuditPage = lazy(() => import('@/modules/audit/pages/AuditPage').then(m => ({ default: m.AuditPage })))
const SettingsPage = lazy(() => import('@/modules/settings/SettingsPage').then(m => ({ default: m.SettingsPage })))
const AdminPage = lazy(() => import('@/modules/admin/pages/AdminPage').then(m => ({ default: m.AdminPage })))
const AdminLayout = lazy(() => import('@/modules/admin/pages/AdminLayout').then(m => ({ default: m.AdminLayout })))
const AdminUsersTab = lazy(() => import('@/modules/admin/components/AdminUsersTab').then(m => ({ default: m.AdminUsersTab })))
const AdminModulesTab = lazy(() => import('@/modules/admin/components/AdminModulesTab').then(m => ({ default: m.AdminModulesTab })))
const AdminLicenseTab = lazy(() => import('@/modules/admin/components/AdminLicenseTab').then(m => ({ default: m.AdminLicenseTab })))
const AdminEmailSettingsTab = lazy(() => import('@/modules/admin/components/AdminEmailSettingsTab').then(m => ({ default: m.AdminEmailSettingsTab })))
const AdminQueueTab = lazy(() => import('@/modules/admin/components/AdminQueueTab').then(m => ({ default: m.AdminQueueTab })))
const AdminOutboxTab = lazy(() => import('@/modules/admin/components/AdminOutboxTab').then(m => ({ default: m.AdminOutboxTab })))
const AdminSessionsTab = lazy(() => import('@/modules/admin/components/AdminSessionsTab').then(m => ({ default: m.AdminSessionsTab })))
const AdminRolesTab = lazy(() => import('@/modules/admin/components/AdminRolesTab').then(m => ({ default: m.AdminRolesTab })))
const AdminJITTab = lazy(() => import('@/modules/admin/components/AdminJITTab').then(m => ({ default: m.AdminJITTab })))
const AdminSystemTab = lazy(() => import('@/modules/admin/components/AdminSystemTab').then(m => ({ default: m.AdminSystemTab })))
const DevComponentsPage = lazy(() => import('@/modules/dev/DevComponentsPage').then(m => ({ default: m.DevComponentsPage })))
const SharedDocumentsPage = lazy(() => import('@/modules/shared/SharedDocumentsPage').then(m => ({ default: m.SharedDocumentsPage })))
const NotificationsPage = lazy(() => import('@/modules/notifications/NotificationsPage').then(m => ({ default: m.NotificationsPage })))
const MonitoringPage = lazy(() => import('@/modules/monitoring/MonitoringPage').then(m => ({ default: m.MonitoringPage })))
const DocsPage = lazy(() => import('@/pages/DocsPage').then(m => ({ default: m.DocsPage })))

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <UploadProvider>
        <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path="/catalog" element={<CatalogPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/documents" element={<Navigate to="/catalog" replace />} />
          <Route path="/projects/:projectId/docs/:folderId?/:docId?" element={<DocumentsPage />} />
          <Route path="/shared" element={<SharedDocumentsPage />} />
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/docs/:section/*" element={<DocsPage />} />
          <Route path="/audit" element={<AuditPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/monitoring" element={<MonitoringPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="users" replace />} />
            <Route path="users" element={<AdminUsersTab />} />
            <Route path="modules" element={<AdminModulesTab />} />
            <Route path="license" element={<AdminLicenseTab />} />
            <Route path="email" element={<AdminEmailSettingsTab />} />
            <Route path="queue" element={<AdminQueueTab />} />
            <Route path="processing" element={<AdminOutboxTab />} />
            <Route path="sessions" element={<AdminSessionsTab />} />
            <Route path="roles" element={<AdminRolesTab />} />
            <Route path="jit" element={<AdminJITTab />} />
            <Route path="system" element={<AdminSystemTab />} />
          </Route>
          {import.meta.env.DEV && <Route path="/dev/components" element={<DevComponentsPage />} />}
        </Route>
        <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        </UploadProvider>
      </ToastProvider>
    </AuthProvider>
  )
}

export default App
